import CompletedBadgeSvg from '^/assets/icons/photo-request/completed-badge.svg';
import DraftBadgeSvg from '^/assets/icons/photo-request/draft-badge.svg';
import FailedBadgeSvg from '^/assets/icons/photo-request/failed-badge.svg';
import RequestedBadgeSvg from '^/assets/icons/photo-request/requested-badge.svg';
import theme from '^/theme';
import { RequestPhotoStatus } from '^/types';
import React, { useMemo } from 'react';
import styled, { CSSObject } from 'styled-components';
import Text from './text';
import { useL10n } from '^/hooks';

interface BadgeContainerProps {
  color: string;
  backgroundColor: string;
  showText?: boolean;
  customStyles?: CSSObject;
}

const BadgeContainer = styled.div<BadgeContainerProps>(
  ({ color, backgroundColor, showText, customStyles }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: color,
    padding: '2px 4px',
    backgroundColor: backgroundColor,
    borderRadius: '4px',
    ...(!showText && {
      padding: '4px',
    }),
    ...customStyles,
  })
);

const StatusText = styled.span({
  fontSize: '12px',
  fontWeight: 600,
});

interface StatusBadgeProps {
  type: RequestPhotoStatus;
  testId?: string;
  showText?: boolean;
  customStyles?: CSSObject;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  type,
  testId,
  showText = true,
  customStyles,
}) => {
  const [l10n] = useL10n();

  const text = l10n(Text[type]);

  const badgeInfo = useMemo(() => {
    switch (type) {
      case RequestPhotoStatus.PENDING:
        return {
          icon: <DraftBadgeSvg />,
          color: '#8E8E8E', // Gray color for Draft
          backgroundColor: theme.colors.neutralGray[50],
        };
      case RequestPhotoStatus.REQUESTED:
        return {
          icon: <RequestedBadgeSvg />,
          color: '#3BA5FC', // Blue color for Requested
          backgroundColor: theme.colors.lightBlue[50],
        };
      case RequestPhotoStatus.COMPLETED:
        return {
          icon: <CompletedBadgeSvg />,
          color: theme.colors.pointGreen[500], // Green color for Completed
          backgroundColor: theme.colors.pointGreen[50],
        };
      case RequestPhotoStatus.FAILED:
        return {
          icon: <FailedBadgeSvg />,
          color: '#FF0000', // Red color for Failed
          backgroundColor: theme.colors.pointRed[50],
        };
      default:
        return null;
    }
  }, [type]);

  if (!badgeInfo) {
    return null;
  }

  return (
    <BadgeContainer
      color={badgeInfo.color}
      backgroundColor={badgeInfo.backgroundColor}
      data-testid={testId}
      showText={showText}
      customStyles={customStyles}
    >
      {badgeInfo.icon}
      {showText && <StatusText>{text}</StatusText>}
    </BadgeContainer>
  );
};
